import { useEffect, useRef, useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { Loader2, Save, Trash2, Eye, EyeOff, Key } from 'lucide-react';
import { useNotification } from '@/components/notifications';
import { Modal } from '@/components/modal';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  ClientApiKey,
  ClientApiKeyUpsert,
  getClientApiKeys,
  upsertClientApiKey,
  deleteClientApiKey,
} from '@/services/admin/client-api-keys';

interface OrgApiKeysTabProps {
  orgId: string;
}

interface ProviderConfig {
  key_name: string;
  service_name: string;
  description: string;
  placeholder: string;
}

const PROVIDERS: ProviderConfig[] = [
  {
    key_name: 'anthropic_key',
    service_name: 'Anthropic',
    description: 'API key for Anthropic services',
    placeholder: 'sk-ant-...',
  },
  {
    key_name: 'openai_key',
    service_name: 'OpenAI',
    description: 'API key for OpenAI services',
    placeholder: 'sk-proj-...',
  },
  {
    key_name: 'openrouter_key',
    service_name: 'OpenRouter',
    description: 'API key for OpenRouter services',
    placeholder: 'sk-or-...',
  },
  {
    key_name: 'gemini_key',
    service_name: 'Gemini',
    description: 'API key for Google Gemini services',
    placeholder: 'AIza...',
  },
  {
    key_name: 'bedrock_key',
    service_name: 'Bedrock',
    description: 'API key for AWS Bedrock services',
    placeholder: 'AKIA...',
  },
  {
    key_name: 'cerebras_key',
    service_name: 'Cerebras',
    description: 'API key for Cerebras services',
    placeholder: 'csk-...',
  },
  {
    key_name: 'perplexity_key',
    service_name: 'Perplexity',
    description: 'API key for Perplexity services',
    placeholder: 'pplx-...',
  },
  {
    key_name: 'reducto_key',
    service_name: 'Reducto',
    description: 'API key for Reducto services',
    placeholder: '',
  },
  {
    key_name: 'github_token',
    service_name: 'GitHub',
    description:
      'Personal access token for GitHub (software repositories). Enables workflow software repositories: code-execution agents clone the configured repo and can commit reusable software back.',
    placeholder: 'ghp_... or github_pat_...',
  },
];

interface ApiKeyProviderCardProps {
  orgId: string;
  provider: ProviderConfig;
  existingKey: ClientApiKey | null;
  onKeyChanged: () => void;
}

const ApiKeyProviderCard = ({
  orgId,
  provider,
  existingKey,
  onKeyChanged,
}: ApiKeyProviderCardProps) => {
  const [apiKeyValue, setApiKeyValue] = useState('');
  const [keyModified, setKeyModified] = useState(false);
  const [active, setActive] = useState(true);
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const notification = useNotification();

  useEffect(() => {
    setApiKeyValue('');
    setKeyModified(false);
    setActive(existingKey?.active ?? true);
    setShowKey(false);
  }, [existingKey]);

  const handleKeyInput = (value: string) => {
    setApiKeyValue(value);
    setKeyModified(true);
  };

  const handleSave = async () => {
    const needsKey = keyModified || !existingKey;
    if (needsKey && !apiKeyValue.trim()) {
      notification.error(
        `Please enter an API key for ${provider.service_name}`,
      );
      return;
    }

    setSaving(true);
    try {
      const payload: ClientApiKeyUpsert = {
        key_name: provider.key_name,
        service_name: provider.service_name,
        description: provider.description,
        active,
        ...(keyModified && apiKeyValue.trim()
          ? { api_key: apiKeyValue.trim() }
          : {}),
      };

      const response = await upsertClientApiKey(orgId, payload);

      if (response?.detail) {
        notification.error(response.detail);
        return;
      }

      notification.success(`${provider.service_name} API key saved`);
      setKeyModified(false);
      setApiKeyValue('');
      onKeyChanged();
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: 'save-client-api-key',
          provider: provider.key_name,
          orgId,
        },
      });
      console.error(`Error saving ${provider.service_name} key:`, error);
      notification.error(`Failed to save ${provider.service_name} API key`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!existingKey) return;

    setDeleting(true);
    try {
      const response = await deleteClientApiKey(orgId, existingKey.id);

      if (response?.detail) {
        notification.error(response.detail);
        return;
      }

      setApiKeyValue('');
      setKeyModified(false);
      setActive(true);
      setShowKey(false);
      setShowDeleteDialog(false);
      notification.success(`${provider.service_name} API key deleted`);
      onKeyChanged();
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: 'delete-client-api-key',
          provider: provider.key_name,
          orgId,
          keyId: existingKey.id,
        },
      });
      console.error(`Error deleting ${provider.service_name} key:`, error);
      notification.error(`Failed to delete ${provider.service_name} API key`);
    } finally {
      setDeleting(false);
    }
  };

  const isProcessing = saving || deleting;
  const hasExistingKey = !!existingKey;

  return (
    <>
      <Card>
        <CardContent className='flex flex-col gap-4'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <Key className='size-4 text-muted-foreground' />
              <span className='font-medium'>
                {provider.service_name} API Key
              </span>
              {existingKey && (
                <Badge
                  variant={active ? 'default' : 'secondary'}
                  className={
                    active
                      ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                      : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
                  }
                >
                  {active ? 'Active' : 'Inactive'}
                </Badge>
              )}
            </div>
          </div>

          <div className='grid gap-4'>
            <div className='grid gap-2'>
              <Label htmlFor={`api-key-${provider.key_name}`}>API Key</Label>
              <div className='relative'>
                <Input
                  id={`api-key-${provider.key_name}`}
                  type={showKey ? 'text' : 'password'}
                  placeholder={
                    hasExistingKey && !keyModified
                      ? '••••••••••••••••'
                      : provider.placeholder
                  }
                  value={apiKeyValue}
                  onChange={(e) => handleKeyInput(e.target.value)}
                  className='pr-10'
                />
                <button
                  type='button'
                  onClick={() => setShowKey(!showKey)}
                  aria-label={showKey ? 'Hide API key' : 'Show API key'}
                  className='absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground'
                >
                  {showKey ? (
                    <EyeOff className='size-4' />
                  ) : (
                    <Eye className='size-4' />
                  )}
                </button>
              </div>
              <p className='text-xs text-muted-foreground'>
                {hasExistingKey && !keyModified
                  ? `${provider.service_name} key is configured. Enter a new value to replace it.`
                  : provider.description}
              </p>
            </div>

            <div className='flex items-center gap-2'>
              <Switch
                id={`active-${provider.key_name}`}
                checked={active}
                onCheckedChange={setActive}
              />
              <Label htmlFor={`active-${provider.key_name}`}>Active</Label>
            </div>
          </div>

          <div className='flex gap-2 pt-2'>
            <Button onClick={handleSave} disabled={isProcessing}>
              {saving ? (
                <>
                  <Loader2 className='size-4 animate-spin' /> Saving...
                </>
              ) : (
                <>
                  <Save className='size-4' /> Save Key
                </>
              )}
            </Button>
            {existingKey && (
              <Button
                variant='outline'
                onClick={() => setShowDeleteDialog(true)}
                disabled={isProcessing}
                className='text-destructive hover:text-destructive'
              >
                <Trash2 className='size-4' /> Delete Key
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Modal
        isDialogOpen={showDeleteDialog}
        setIsDialogOpen={setShowDeleteDialog}
        title={`Delete ${provider.service_name} API Key`}
        description={`This will permanently remove the ${provider.service_name} API key for this organization. Any services using this key will stop working. This action cannot be undone.`}
        footer={
          <>
            <Button
              variant='outline'
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Loader2 className='size-4 animate-spin' /> Deleting...
                </>
              ) : (
                'Delete Key'
              )}
            </Button>
          </>
        }
      />
    </>
  );
};

export const OrgApiKeysTab = ({ orgId }: OrgApiKeysTabProps) => {
  const [keys, setKeys] = useState<ClientApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const notification = useNotification();
  const notificationRef = useRef(notification);
  notificationRef.current = notification;

  const fetchKeys = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getClientApiKeys(orgId);

      if (response?.detail) {
        notificationRef.current.error(response.detail);
        return;
      }

      if (response?.data) {
        setKeys(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: 'fetch-client-api-keys', orgId },
      });
      console.error('Error fetching API keys:', error);
      notificationRef.current.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    if (orgId) {
      fetchKeys();
    }
  }, [orgId, fetchKeys]);

  const getExistingKey = (keyName: string): ClientApiKey | null => {
    return keys.find((k) => k.key_name === keyName) || null;
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <Loader2 className='size-6 animate-spin text-muted-foreground' />
        <span className='ml-2 text-muted-foreground'>Loading API keys...</span>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-4'>
      {PROVIDERS.map((provider) => (
        <ApiKeyProviderCard
          key={provider.key_name}
          orgId={orgId}
          provider={provider}
          existingKey={getExistingKey(provider.key_name)}
          onKeyChanged={fetchKeys}
        />
      ))}
    </div>
  );
};
